'use client';

import { useEffect } from 'react';

// Parse stack trace to find original source file
function parseStackTrace(stack: string | undefined): { file: string; line: number; column: number } | null {
  if (!stack) return null;

  // Split stack trace into lines
  const lines = stack.split('\n');
  
  // Look for the first line that contains a file path from our source code
  for (const line of lines) {
    // Match patterns like:
    // at ComponentName (webpack-internal:///./src/path/to/file.tsx:123:45)
    // at webpack-internal:///./src/path/to/file.tsx:123:45
    // at /src/path/to/file.tsx:123:45
    const match = line.match(/(?:webpack-internal:\/\/\/)?\.?\/?(\S+\.(tsx?|jsx?)):(\d+):(\d+)/);
    
    if (match) {
      const [, filePath, , lineNum, colNum] = match;
      
      // Filter out node_modules and Next.js internal files
      if (!filePath.includes('node_modules') && 
          !filePath.includes('next/dist') &&
          !filePath.includes('react-dom') &&
          !filePath.includes('webpack')) {
        return {
          file: filePath.replace(/^\.\//, ''), // Remove leading ./
          line: parseInt(lineNum, 10),
          column: parseInt(colNum, 10)
        };
      }
    }
  }
  
  return null;
}

export function ErrorTracker() {
  useEffect(() => {
    // Global error listener for unhandled errors
    const handleError = (event: ErrorEvent) => {
      // Try to parse the stack trace for original source location
      const sourceLocation = parseStackTrace(event.error?.stack);
      
      console.group('🔴 Global Error Caught');
      console.error('Message:', event.message);
      
      if (sourceLocation) {
        console.error('📁 Original Source File:', sourceLocation.file);
        console.error('📍 Line:', sourceLocation.line);
        console.error('📍 Column:', sourceLocation.column);
      } else {
        console.error('Bundled Filename:', event.filename);
        console.error('Line:', event.lineno);
        console.error('Column:', event.colno);
      }
      
      console.error('Error object:', event.error);
      console.error('Full Stack trace:', event.error?.stack);
      console.groupEnd();
    };

    // Global promise rejection listener
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      // Try to parse the stack trace for original source location
      const sourceLocation = parseStackTrace(event.reason?.stack);
      
      console.group('🔴 Unhandled Promise Rejection');
      console.error('Reason:', event.reason);
      
      if (sourceLocation) {
        console.error('📁 Original Source File:', sourceLocation.file);
        console.error('📍 Line:', sourceLocation.line);
        console.error('📍 Column:', sourceLocation.column);
      }
      
      console.error('Promise:', event.promise);
      if (event.reason?.stack) {
        console.error('Full Stack trace:', event.reason.stack);
      }
      console.groupEnd();
    };

    // React error boundary fallback
    const handleReactError = (event: Event) => {
      console.group('🔴 React Error');
      console.error('Event:', event);
      console.groupEnd();
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('react-error', handleReactError);

    // console.log('✅ Error tracking initialized');

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('react-error', handleReactError);
    };
  }, []);

  return null;
}
